import './index.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

import { KindeProvider } from '@kinde-oss/kinde-auth-react';
import { ErrorBoundary } from '@sentry/react';
import { enableMapSet } from 'immer';
import mixpanel from 'mixpanel-browser';
import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { pdfjs } from 'react-pdf';

import { ErrorCard } from '@/components/core';
import { checkHasRequiredClaims, ClaimsWithPermissions } from '@/features/auth';

import { App } from './App';

const originalURL = globalThis.URL;

// https://medium.com/@edekobifrank/fix-for-pdfjs-error-failed-to-load-pdf-url-parse-is-not-a-function-346d08ff8abb
globalThis.URL = class URL extends originalURL {
  constructor(url: string | URL, base?: string | URL) {
    let finalUrl: string | URL = url;

    if (typeof url === 'string' && url.startsWith('blob:')) {
      finalUrl = url;
    }

    super(finalUrl as string, base as string);
  }

  static parse(url: string, base?: string): URL | null {
    try {
      return new URL(url, base);
    } catch {
      return null;
    }
  }
} as typeof originalURL;

// react-pdf setup
pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

enableMapSet();

const rootElement = document.getElementById('root')!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <ErrorBoundary
        fallback={(e) => {
          console.error('Error triggering fallback', e);

          return (
            <div className="flex h-screen w-screen items-center justify-center">
              <div className="gap-sm flex h-32 w-64 flex-col">
                <ErrorCard label="Oops something went wrong" error={e.error instanceof Error ? e.error : undefined} />
              </div>
            </div>
          );
        }}
      >
        <KindeProvider
          clientId={import.meta.env.VITE_AUTH_CLIENT_ID}
          domain={import.meta.env.VITE_AUTH_ENDPOINT}
          redirectUri={import.meta.env.VITE_AUTH_REDIRECT_URI}
          logoutUri={import.meta.env.VITE_LOGOUT_REDIRECT_URI}
          callbacks={{
            onSuccess: async (user, state, context) => {
              const claims: ClaimsWithPermissions | null = await context.getClaims();
              if (!checkHasRequiredClaims(claims)) {
                setTimeout(context.login, 2500);
              }

              if (claims != null) {
                const profileId = claims['user_properties']?.profile_id?.v;
                if (profileId) {
                  try {
                    mixpanel.identify(profileId);
                  } catch (error) {
                    console.error(error);
                  }
                }
              }
            },
            onError: async (error, state, context) => {
              console.error('Kinde authentication error', error);
              const path = window.location.href.replace(window.location.origin, '');
              console.log('Redirecting to login with redirect path = ', path);
              window.location.href = `/login?redirect=${encodeURIComponent(path)}`;
            },
          }}
        >
          <App />
        </KindeProvider>
      </ErrorBoundary>
    </StrictMode>
  );
}
